import {useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import {Alert, Button, Offcanvas} from 'react-bootstrap'
import {toast} from 'react-toastify'

import {
  TELEPHONY_DRAWER_CLOSE_EVENT,
  TELEPHONY_DRAWER_OPEN_EVENT,
  TELEPHONY_DRAWER_UPDATE_EVENT,
  TelephonyDrawerOpenDetail,
  TelephonyDrawerDiagnostics,
  TELEPHONY_VICIDIAL_LOGOUT_EVENT,
} from '../../types/telephony'
import {URL} from '../../gql/client'
import {STORAGE_NAMES} from '../../helpers/consts'

const VICIDIAL_EXTERNAL_WINDOW_KEY = '__policytoolsVicidialWebphoneWindow'

export const TelephonyDrawer = () => {
  const [show, setShow] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [drawerState, setDrawerState] = useState<TelephonyDrawerOpenDetail>({
    url: '',
    title: 'Webphone',
  })

  const shouldForceExternalOpen = useMemo(() => {
    if (!drawerState.url || typeof window === 'undefined') return false
    return window.location.protocol !== 'https:' && drawerState.url.startsWith('https://')
  }, [drawerState.url])

  const openInNewTab = () => {
    if (!drawerState.url) return
    const externalWindow = window.open(drawerState.url, 'PolicyToolsVicidialWebphone')
    if (externalWindow) {
      ;(window as any)[VICIDIAL_EXTERNAL_WINDOW_KEY] = externalWindow
      externalWindow.focus?.()
    }
  }

  const closeTrackedExternalWindow = () => {
    let externalWindow = (window as any)[VICIDIAL_EXTERNAL_WINDOW_KEY] as Window | null | undefined
    if (!externalWindow) {
      externalWindow = window.open('', 'PolicyToolsVicidialWebphone')
    }
    if (externalWindow && !externalWindow.closed && externalWindow.location.href !== 'about:blank') {
      externalWindow.close()
    }
    ;(window as any)[VICIDIAL_EXTERNAL_WINDOW_KEY] = null
  }

  const handleVicidialLogout = async () => {
    const token = localStorage.getItem(STORAGE_NAMES.TOKEN)
    if (!token) {
      toast.error('Your session expired. Log in again to disconnect VICIdial.')
      return
    }

    setDisconnecting(true)
    try {
      await axios.post(
        `${URL}/api/telephony/disconnect-agent`,
        {provider: 'VICIDIAL'},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      localStorage.removeItem(STORAGE_NAMES.VICIDIAL_SESSION)
      localStorage.setItem(STORAGE_NAMES.VICIDIAL_FORCE_DISCONNECTED, '1')
      window.dispatchEvent(new CustomEvent(TELEPHONY_VICIDIAL_LOGOUT_EVENT))
      
      const channel = new BroadcastChannel('policytools_vicidial_channel')
      channel.postMessage('CLOSE_WEBPHONE')
      channel.close()
      
      closeTrackedExternalWindow()
      setShow(false)
      toast.info(
        shouldForceExternalOpen
          ? 'VICIdial disconnected. If the secure VICIdial page still remains open in another browser tab, use the native LOGOUT link there once.'
          : 'VICIdial disconnected.'
      )
    } catch (error: any) {
      const message = error?.response?.data?.error || 'Unable to log out from VICIdial.'
      toast.error(message)
    } finally {
      setDisconnecting(false)
    }
  }

  useEffect(() => {
    const handleOpen = (event: Event) => {
      const customEvent = event as CustomEvent<TelephonyDrawerOpenDetail>
      if (!customEvent.detail?.url) return

      setDrawerState(customEvent.detail)
      setShow(true)
    }

    const handleUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{diagnostics?: TelephonyDrawerDiagnostics}>
      if (!customEvent.detail?.diagnostics) return

      setDrawerState((current) => ({
        ...current,
        diagnostics: {
          ...(current.diagnostics || {}),
          ...customEvent.detail.diagnostics,
        },
      }))
    }

    const handleClose = () => {
      setShow(false)
    }

    window.addEventListener(TELEPHONY_DRAWER_OPEN_EVENT, handleOpen as EventListener)
    window.addEventListener(TELEPHONY_DRAWER_UPDATE_EVENT, handleUpdate as EventListener)
    window.addEventListener(TELEPHONY_DRAWER_CLOSE_EVENT, handleClose as EventListener)

    const channel = new BroadcastChannel('policytools_vicidial_channel')
    channel.onmessage = (event) => {
      if (event.data === 'CLOSE_WEBPHONE') {
        closeTrackedExternalWindow()
        setShow(false)
      }
    }

    return () => {
      window.removeEventListener(TELEPHONY_DRAWER_OPEN_EVENT, handleOpen as EventListener)
      window.removeEventListener(TELEPHONY_DRAWER_UPDATE_EVENT, handleUpdate as EventListener)
      window.removeEventListener(TELEPHONY_DRAWER_CLOSE_EVENT, handleClose as EventListener)
      channel.close()
    }
  }, [])

  return (
    <Offcanvas
      show={show}
      onHide={() => setShow(false)}
      placement='end'
      style={{width: drawerState.width || '460px'}}
    >
      <Offcanvas.Header closeButton>
        <div className='d-flex flex-column'>
          <Offcanvas.Title>{drawerState.title || 'Webphone'}</Offcanvas.Title>
          {drawerState.warning && (
            <span className='text-muted fs-8 mt-1'>{drawerState.warning}</span>
          )}
        </div>
      </Offcanvas.Header>
      <Offcanvas.Body className='p-0 d-flex flex-column'>
        <div className='border-bottom p-3 d-flex justify-content-end gap-2'>
          {drawerState.provider === 'VICIDIAL' && (
            <Button
              variant='light-danger'
              size='sm'
              onClick={handleVicidialLogout}
              disabled={disconnecting}
            >
              {disconnecting ? 'Logging out...' : 'Logout'}
            </Button>
          )}
          <Button
            variant='light-primary'
            size='sm'
            onClick={openInNewTab}
            disabled={!drawerState.url || disconnecting}
          >
            {shouldForceExternalOpen ? 'Open secure webphone' : 'Open in new tab'}
          </Button>
        </div>
        {shouldForceExternalOpen && (
          <Alert variant='warning' className='m-3 mb-0'>
            Your current app session is running over HTTP. ViciPhone rejects embedded access from
            non-encrypted referrers like <code>localhost</code>. Use <strong>Open secure webphone</strong>{' '}
            to continue in a separate HTTPS tab.
          </Alert>
        )}
        {drawerState.diagnostics && (
          <div className='border-bottom px-3 py-2 bg-light'>
            {drawerState.diagnostics.message && (
              <div className='fw-semibold mb-2'>{drawerState.diagnostics.message}</div>
            )}
            <div className='d-flex flex-wrap gap-3 text-muted fs-8'>
              {drawerState.diagnostics.agent_user && (
                <span>Agent: {drawerState.diagnostics.agent_user}</span>
              )}
              {drawerState.diagnostics.status && (
                <span>Status: {drawerState.diagnostics.status}</span>
              )}
              {drawerState.diagnostics.campaign_id && (
                <span>Campaign: {drawerState.diagnostics.campaign_id}</span>
              )}
              {drawerState.diagnostics.session_id && (
                <span>Session: {drawerState.diagnostics.session_id}</span>
              )}
              {drawerState.diagnostics.phone_number && (
                <span>Phone: {drawerState.diagnostics.phone_number}</span>
              )}
            </div>
            {drawerState.diagnostics.raw_response && (
              <pre
                className='mt-2 mb-0 p-2 rounded bg-white border'
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: '120px',
                  overflow: 'auto',
                  fontSize: '11px',
                }}
              >
                {drawerState.diagnostics.raw_response}
              </pre>
            )}
          </div>
        )}
        {drawerState.url && !shouldForceExternalOpen ? (
          <iframe
            src={drawerState.url}
            title={drawerState.title || 'Webphone'}
            allow='microphone; autoplay'
            referrerPolicy='no-referrer'
            style={{border: 0, width: '100%', height: '100%'}}
          />
        ) : null}
      </Offcanvas.Body>
    </Offcanvas>
  )
}
